import { ADHESION_DESKTOP_CSS_VAR, ADHESION_EVENT, ADHESION_MOBILE_CSS_VAR } from '@/defs/ad'
import {
  HEADER_HEIGHT_CSS_VAR,
  HEADER_HEIGHT_FALLBACK,
  HEIGHT_AVAILABLE_CSS_VAR,
  type HeightAvailableConfigType,
} from '@/defs/observer'

let HEIGHT_AVAILABLE_CONFIG: HeightAvailableConfigType | undefined

const getHeaderHeight = () => {
  const probe = document.createElement('div')
  probe.style.cssText = `position:absolute;visibility:hidden;height:var(${HEADER_HEIGHT_CSS_VAR})`
  document.body.appendChild(probe)
  const { height } = probe.getBoundingClientRect()
  probe.remove()

  return height || HEADER_HEIGHT_FALLBACK
}

const getAdhesionHeight = () => {
  const style = getComputedStyle(document.documentElement)
  return Math.ceil(
    (parseFloat(style.getPropertyValue(ADHESION_DESKTOP_CSS_VAR)) || 0) +
      (parseFloat(style.getPropertyValue(ADHESION_MOBILE_CSS_VAR)) || 0),
  )
}

const buildHeightAvailableConfig = (): HeightAvailableConfigType => {
  const heightsMap = new Map<HTMLElement, number | undefined>()
  let frame = 0
  let isHeaderStale = true
  let headerHeight = 0
  let isAdhesionStale = true
  let adhesionHeight = 0

  const update = () => {
    frame = 0

    if (isHeaderStale) {
      headerHeight = getHeaderHeight()
      isHeaderStale = false
    }

    if (isAdhesionStale) {
      adhesionHeight = getAdhesionHeight()
      isAdhesionStale = false
    }

    const viewportBottom = window.innerHeight - adhesionHeight
    const measuredElements = [...heightsMap.keys()].map((el) => {
      const { bottom, top } = el.getBoundingClientRect()
      const available = Math.floor(Math.max(0, Math.min(viewportBottom, bottom) - Math.max(top, headerHeight)))
      return { available, el }
    })

    measuredElements.forEach(({ available, el }) => {
      if (heightsMap.get(el) === available) return
      heightsMap.set(el, available)
      el.style.setProperty(HEIGHT_AVAILABLE_CSS_VAR, `${available}px`)
    })
  }

  const schedule = () => {
    if (frame) return
    frame = requestAnimationFrame(update)
  }

  const onResize = () => {
    isHeaderStale = true
    schedule()
  }

  const onAdhesion = () => {
    isAdhesionStale = true
    schedule()
  }

  const observer = new ResizeObserver(schedule)

  const start = () => {
    isHeaderStale = true
    isAdhesionStale = true
    observer.observe(document.documentElement)
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', onResize)
    window.addEventListener(ADHESION_EVENT, onAdhesion)
  }

  const stop = () => {
    if (frame) cancelAnimationFrame(frame)
    frame = 0
    observer.disconnect()
    window.removeEventListener('scroll', schedule)
    window.removeEventListener('resize', onResize)
    window.removeEventListener(ADHESION_EVENT, onAdhesion)
  }

  const addElement = (el: HTMLElement) => {
    if (heightsMap.size < 1) start()
    heightsMap.set(el, undefined)
    observer.observe(el)
    schedule()
  }

  const deleteElement = (el: HTMLElement) => {
    if (!heightsMap.delete(el)) return
    observer.unobserve(el)
    el.style.removeProperty(HEIGHT_AVAILABLE_CSS_VAR)
    if (heightsMap.size < 1) stop()
  }

  return { addElement, deleteElement }
}

export const getHeightAvailableConfig = () => {
  if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return
  if (!HEIGHT_AVAILABLE_CONFIG) HEIGHT_AVAILABLE_CONFIG = buildHeightAvailableConfig()

  return HEIGHT_AVAILABLE_CONFIG
}
